import { useCallback, useEffect, useState } from 'react';
import { calendarEventsQuery, subscribe } from '@/lib/firestore';
import { log } from '@/lib/logger';
import type { EventDoc } from '@/schemas';

export type EventWithId = EventDoc & { id: string };

export function useEvents(aid: string | null, cid: string | null): {
  events: EventWithId[];
  loading: boolean;
  /** True when the listener has given up (see subscribe's onError) —
   * `events` may be stale or empty. `retry` re-attaches. */
  error: boolean;
  retry: () => void;
} {
  // Derived loading: see useMyMemberships for the why. Multi-arg key
  // combined into a single comparable string so we still get the
  // stale-state-during-render protection across both aid + cid.
  const key = aid && cid ? `${aid}|${cid}` : null;
  const [state, setState] = useState<{ loadedKey: string | null; data: EventWithId[] }>({
    loadedKey: null,
    data: [],
  });
  const [error, setError] = useState(false);
  // Bumping the nonce re-runs the effect → fresh subscribe with fresh
  // reconnect budget.
  const [retryNonce, setRetryNonce] = useState(0);
  const retry = useCallback(() => {
    setError(false);
    setRetryNonce((n) => n + 1);
  }, []);
  useEffect(() => {
    if (!aid || !cid) {
      setState({ loadedKey: null, data: [] });
      return;
    }
    setState({ loadedKey: null, data: [] });
    const unsub = subscribe<EventDoc>(
      calendarEventsQuery(aid, cid),
      (rows) => {
        setError(false);
        const sorted = [...rows].sort((a, b) => {
          const aMs = (a.startsAt as { toMillis?: () => number })?.toMillis?.() ?? 0;
          const bMs = (b.startsAt as { toMillis?: () => number })?.toMillis?.() ?? 0;
          return aMs - bMs;
        });
        setState({ loadedKey: `${aid}|${cid}`, data: sorted });
      },
      (err) => {
        log.error('events listener failed', { aid, cid, error: String(err) });
        setError(true);
      },
    );
    return unsub;
  }, [aid, cid, retryNonce]);
  return {
    events: state.loadedKey === key ? state.data : [],
    loading: key != null && state.loadedKey !== key && !error,
    error,
    retry,
  };
}
